/** Admin: generic entity list page + admin dashboard with live data. */
import { useEffect, useState } from "react";
import { Route, Routes, Link } from "react-router-dom";
import {
  LayoutGrid, BookOpen, Gem, Library, FlaskConical, Compass, FileText,
  Image as ImageIcon, Bell, Settings as Cog, Users as UsersIcon, ShieldCheck,
  ScrollText, Activity, Home, BarChart3, ShoppingBag, Calendar, Upload, Play, Mail,
} from "lucide-react";
import { DashboardLayout } from "./DashboardLayout";
import { EntityFormModal } from "./EntityFormModal";
import {
  Eyebrow, PageHeader, EmptyState, Card, CardBody, Badge, LoadingState, ErrorState,
  SearchInput, Pagination, Button, PriceBlock,
} from "../../components/ui";
import { api, formatApiErrorDetail } from "../../lib/api";

const ADMIN_NAV = [
  { label: "Overview", items: [
    { to: "/admin", label: "Dashboard", Icon: LayoutGrid, exact: true },
    { to: "/admin/system-health", label: "System Health", Icon: ShieldCheck },
  ]},
  { label: "Content", items: [
    { to: "/admin/homepage", label: "Homepage", Icon: Home },
    { to: "/admin/banners", label: "Hero banners", Icon: ImageIcon },
    { to: "/admin/brochures", label: "Brochures", Icon: FileText },
    { to: "/admin/categories", label: "Categories", Icon: LayoutGrid },
    { to: "/admin/legal", label: "Legal pages", Icon: ScrollText },
    { to: "/admin/media", label: "Media library", Icon: ImageIcon },
  ]},
  { label: "Catalog", items: [
    { to: "/admin/courses", label: "Courses", Icon: BookOpen },
    { to: "/admin/sadhana", label: "Sadhana", Icon: Compass },
    { to: "/admin/products", label: "Products", Icon: Gem },
    { to: "/admin/library", label: "Library", Icon: Library },
    { to: "/admin/research", label: "Research", Icon: FlaskConical },
    { to: "/admin/services", label: "Services", Icon: Compass },
    { to: "/admin/videos", label: "Videos", Icon: Play },
  ]},
  { label: "Commerce", items: [
    { to: "/admin/orders", label: "Orders", Icon: ShoppingBag },
    { to: "/admin/bookings", label: "Bookings", Icon: Calendar },
    { to: "/admin/enquiries", label: "Enquiries", Icon: Mail },
  ]},
  { label: "People", items: [
    { to: "/admin/users", label: "Users", Icon: UsersIcon },
    { to: "/admin/roles", label: "Roles", Icon: ShieldCheck },
  ]},
  { label: "Operations", items: [
    { to: "/admin/audit", label: "Audit log", Icon: Activity },
    { to: "/admin/settings", label: "Settings", Icon: Cog },
  ]},
];

function useFetch(path) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { setData(null); setErr(null); api.get(path).then(r => setData(r.data)).catch(e => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message)); }, [path]);
  return { data, err, reload: () => { setData(null); api.get(path).then(r => setData(r.data)); } };
}

function AdminOverview() {
  const { data, err } = useFetch("/dashboard/admin");
  if (err) return <ErrorState description={err} />;
  if (!data) return <LoadingState />;
  return (
    <>
      <Eyebrow>Command center</Eyebrow>
      <h1 className="font-display text-[clamp(30px,3.4vw,42px)] mt-3 leading-tight">NVL — Administration</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
        <Card variant="elevated"><CardBody><div className="text-[11px] font-mono uppercase tracking-[0.16em] text-text-dim">Revenue (all-time)</div><div className="font-display text-[32px] mt-1"><PriceBlock price={data.revenue.total_inr} /></div><div className="text-[12px] text-text-faint mt-1">{data.revenue.orders} orders</div></CardBody></Card>
        <Card variant="elevated"><CardBody><div className="text-[11px] font-mono uppercase tracking-[0.16em] text-text-dim">Revenue (30 days)</div><div className="font-display text-[32px] mt-1"><PriceBlock price={data.revenue.last_30_total_inr} /></div><div className="text-[12px] text-text-faint mt-1">{data.revenue.last_30_orders} orders</div></CardBody></Card>
        <Card variant="elevated"><CardBody><div className="text-[11px] font-mono uppercase tracking-[0.16em] text-text-dim">Users</div><div className="font-display text-[32px] mt-1">{data.counts.users}</div></CardBody></Card>
        <Card variant="elevated"><CardBody><div className="text-[11px] font-mono uppercase tracking-[0.16em] text-text-dim">Enrollments</div><div className="font-display text-[32px] mt-1">{data.counts.enrollments}</div></CardBody></Card>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-3">
        {Object.entries(data.counts).filter(([k]) => !["users", "enrollments", "media"].includes(k)).map(([k, v]) => (
          <Card key={k} variant="default"><CardBody className="py-4"><div className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-text-dim">{k}</div><div className="font-display text-[24px]">{v}</div></CardBody></Card>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
        <div>
          <PageHeader eyebrow="Recent" title="Orders" />
          {data.recent_orders.length === 0 ? <EmptyState title="No orders yet" /> : (
            <Card variant="elevated"><CardBody className="p-0"><ul className="divide-y divide-line">
              {data.recent_orders.map(o => (
                <li key={o.id} className="px-5 py-3 flex items-center gap-3 text-[13.5px]">
                  <span className="font-mono text-[11px] text-text-faint">{o.number}</span>
                  <span className="flex-1 truncate">{o.name}</span>
                  <Badge variant={o.status === "paid" ? "success" : "muted"} size="sm">{o.status}</Badge>
                  <PriceBlock price={o.total_inr} size="sm" />
                </li>
              ))}
            </ul></CardBody></Card>
          )}
        </div>
        <div>
          <PageHeader eyebrow="Recent" title="Audit" />
          {data.recent_audit.length === 0 ? <EmptyState title="Nothing logged" /> : (
            <Card variant="elevated"><CardBody className="p-0"><ul className="divide-y divide-line">
              {data.recent_audit.map(a => (
                <li key={a.id} className="px-5 py-3 text-[13.5px]">
                  <div className="flex items-center gap-2"><Badge size="sm" variant="info">{a.action}</Badge><span className="text-text-faint font-mono text-[11px]">{a.target_kind}</span></div>
                  <div className="text-[12.5px] text-text-dim mt-1">{a.summary || `${a.actor_email || "system"} · ${a.action}`}</div>
                </li>
              ))}
            </ul></CardBody></Card>
          )}
        </div>
      </div>
    </>
  );
}

function EntityList({ title, listPath, detailPath, kind }) {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [modal, setModal] = useState(null); // null | { initial: {} | item }
  const editable = !["order", "booking"].includes(kind);

  const load = () => {
    setData(null); setErr(null);
    const qs = new URLSearchParams({ page, limit: 20 });
    if (q) qs.set("q", q);
    api.get(`${listPath}?${qs}`).then(r => setData(r.data)).catch(e => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };
  useEffect(load, [listPath, page, q]);  // eslint-disable-line

  const onDelete = async (id) => {
    if (!window.confirm("Archive this item?")) return;
    await api.delete(`${listPath}/${id}`);
    load();
  };

  return (
    <>
      <PageHeader eyebrow="Catalog" title={title}
        actions={editable ? <Button onClick={() => setModal({ initial: {} })} data-testid={`admin-new-${kind}`}>+ New {kind}</Button> : null} />
      <SearchInput value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder={`Search ${title.toLowerCase()}…`} className="max-w-[360px] mb-5" testId={`admin-search-${kind}`} />
      {err && <ErrorState description={err} retry={load} />}
      {!err && !data && <LoadingState />}
      {data && data.items.length === 0 && <EmptyState title="No items" description="Use the seed data or create a new entry." />}
      {data && data.items.length > 0 && (
        <>
          <Card variant="elevated"><CardBody className="p-0"><table className="w-full text-[13.5px]" data-testid={`admin-table-${kind}`}>
            <thead><tr className="text-left border-b border-line">
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim">Title</th>
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim">Status</th>
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim">Featured</th>
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim text-right">Price</th>
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim text-right">Actions</th>
            </tr></thead>
            <tbody>
              {data.items.map(it => (
                <tr key={it.id} className="border-b border-line hover:bg-surface transition-colors" data-testid={`row-${it.slug || it.id}`}>
                  <td className="px-4 py-3"><div className="font-semibold">{it.title || it.number || it.id}</div><div className="text-[11.5px] text-text-faint font-mono">{it.slug || ""}</div></td>
                  <td className="px-4 py-3"><Badge variant={it.status === "published" || it.status === "paid" ? "success" : "muted"} size="sm">{it.status}</Badge></td>
                  <td className="px-4 py-3">{it.featured ? "★" : ""}</td>
                  <td className="px-4 py-3 text-right">{it.is_free ? "Free" : it.price_inr > 0 ? <PriceBlock price={it.price_inr} size="sm" /> : (it.total_inr ? <PriceBlock price={it.total_inr} size="sm" /> : "—")}</td>
                  <td className="px-4 py-3 text-right space-x-3">
                    {detailPath && <Link to={detailPath.replace(":slug", it.slug)} className="text-mauve hover:underline text-[12px]">View</Link>}
                    {editable && <button onClick={() => setModal({ initial: it })} className="text-mauve hover:underline text-[12px]" data-testid={`edit-${it.slug}`}>Edit</button>}
                    {editable && <button onClick={() => onDelete(it.id)} className="text-red-400 hover:underline text-[12px]" data-testid={`delete-${it.slug}`}>Archive</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></CardBody></Card>
          {data.meta.page_count > 1 && <div className="mt-6 flex justify-center"><Pagination page={data.meta.page} pageCount={data.meta.page_count} onPageChange={setPage} /></div>}
        </>
      )}
      {modal && editable && <EntityFormModal kind={kind} initial={modal.initial} onClose={() => setModal(null)} onSaved={load} />}
    </>
  );
}

function EnquiriesAdmin() {
  const [page, setPage] = useState(1);
  const { data, err, reload } = useFetch(`/admin/enquiries?page=${page}&limit=20`);
  const setStatus = async (id, status) => { await api.patch(`/admin/enquiries/${id}`, { status }); reload(); };
  if (err) return <ErrorState description={err} />;
  if (!data) return <LoadingState />;
  return (
    <>
      <PageHeader eyebrow="Inbox" title="Enquiries" lede="Messages submitted through the Contact page. Stored here even when email is not configured." />
      {data.items.length === 0 ? <EmptyState title="No enquiries yet" /> : (
        <div className="space-y-3" data-testid="admin-enquiries-list">
          {data.items.map((e) => (
            <Card key={e.id} variant="elevated"><CardBody className="p-5" data-testid={`enquiry-${e.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <div className="font-semibold text-[14px]">{e.name} <span className="text-text-faint font-normal">· {e.topic}</span></div>
                  <div className="text-[12.5px] text-text-dim"><a href={`mailto:${e.email}`} className="hover:text-mauve">{e.email}</a>{e.phone ? ` · ${e.phone}` : ""}</div>
                </div>
                <span className="text-[11.5px] text-text-faint">{new Date(e.created_at).toLocaleString()}</span>
                <select value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)} data-testid={`enquiry-status-${e.id}`}
                  className="bg-surface border border-line-2 rounded-full px-3 py-1.5 text-[12.5px]">
                  {["new", "read", "replied", "archived"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {e.subject && <div className="mt-3 font-display text-[17px]">{e.subject}</div>}
              <p className="mt-2 text-[13.5px] text-text-dim whitespace-pre-wrap">{e.message}</p>
            </CardBody></Card>
          ))}
        </div>
      )}
      {data.meta.page_count > 1 && <div className="mt-6 flex justify-center"><Pagination page={data.meta.page} pageCount={data.meta.page_count} onPageChange={setPage} /></div>}
    </>
  );
}

function UsersList() {
  const { data, err } = useFetch("/users");
  if (err) return <ErrorState description={err} />;
  if (!data) return <LoadingState />;
  return (
    <>
      <PageHeader eyebrow="People" title="Users" />
      <Card variant="elevated"><CardBody className="p-0"><table className="w-full text-[13.5px]" data-testid="admin-users-table">
        <thead><tr className="text-left border-b border-line">
          <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim">Name</th>
          <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim">Email</th>
          <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim">Role</th>
          <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim">Status</th>
        </tr></thead>
        <tbody>{data.items.map(u => (
          <tr key={u.id} className="border-b border-line">
            <td className="px-4 py-3 font-semibold">{u.name || "—"}</td>
            <td className="px-4 py-3 text-text-dim">{u.email}</td>
            <td className="px-4 py-3"><Badge variant="info" size="sm">{u.role}</Badge></td>
            <td className="px-4 py-3"><Badge variant={u.status === "active" ? "success" : "muted"} size="sm">{u.status}</Badge></td>
          </tr>
        ))}</tbody>
      </table></CardBody></Card>
    </>
  );
}

function AuditList() {
  const { data, err } = useFetch("/audit?limit=50");
  if (err) return <ErrorState description={err} />;
  if (!data) return <LoadingState />;
  return (
    <>
      <PageHeader eyebrow="Operations" title="Audit log" lede="Every administrative write recorded with actor, action and target." />
      <Card variant="elevated"><CardBody className="p-0"><ul className="divide-y divide-line" data-testid="audit-list">
        {data.items.map(a => (
          <li key={a.id} className="px-5 py-3">
            <div className="flex items-center gap-2 text-[13px]"><Badge size="sm" variant="info">{a.action}</Badge>
              <span className="font-mono text-[11px] text-text-faint">{a.target_kind}</span>
              <span className="ml-auto text-[11.5px] text-text-faint">{new Date(a.created_at).toLocaleString()}</span>
            </div>
            <div className="text-[12.5px] text-text-dim mt-1">{a.summary || `${a.actor_email || "system"} performed ${a.action}`}</div>
          </li>
        ))}
      </ul></CardBody></Card>
    </>
  );
}

function SettingsAdmin() {
  const [data, setData] = useState(null);
  const [key, setKey] = useState("general");
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setData(null); setMsg(null);
    api.get(`/settings/${key}`).then((r) => setData(r.data)).catch(() => setData({ data: {} }));
  };
  useEffect(load, [key]);

  const update = (path, val) => {
    setData((d) => {
      const next = { ...d, data: { ...(d?.data || {}) } };
      next.data[path] = val;
      return next;
    });
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try { await api.put(`/settings/${key}`, data.data); setMsg("Saved."); }
    catch (e) { setMsg(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader eyebrow="Configuration" title="Settings" lede="Platform-wide configuration values." />
      <div className="flex gap-2 flex-wrap mb-6" data-testid="settings-tabs">
        {["general", "branding", "contact", "social", "login_page", "about_links", "seo_defaults", "email", "payments", "feature_flags"].map(k => (
          <Button key={k} size="sm" variant={k === key ? "primary" : "ghost"} onClick={() => setKey(k)} data-testid={`settings-tab-${k}`}>{k}</Button>
        ))}
      </div>
      {!data ? <LoadingState /> : (
        <Card variant="elevated"><CardBody className="p-7 space-y-4">
          {Object.entries(data.data || {}).map(([k, v]) => {
            const isBool = typeof v === "boolean";
            const isObjOrArr = v && typeof v === "object";
            return (
              <div key={k}>
                <label className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim block mb-1">{k}</label>
                {isBool ? (
                  <label className="flex items-center gap-2 text-[14px]">
                    <input type="checkbox" checked={!!v} onChange={(e) => update(k, e.target.checked)} data-testid={`settings-field-${k}`} />
                    <span className="text-text-dim">{k}</span>
                  </label>
                ) : isObjOrArr ? (
                  <textarea rows={5} value={JSON.stringify(v, null, 2)}
                    onChange={(e) => { try { update(k, JSON.parse(e.target.value)); } catch { /* keep typing */ } }}
                    className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[13px] font-mono focus:outline-none focus:border-mauve"
                    data-testid={`settings-field-${k}`} />
                ) : (
                  <input type={typeof v === "number" ? "number" : "text"} value={v ?? ""}
                    onChange={(e) => update(k, typeof v === "number" ? Number(e.target.value) : e.target.value)}
                    className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-mauve"
                    data-testid={`settings-field-${k}`} />
                )}
              </div>
            );
          })}
          {Object.keys(data.data || {}).length === 0 && (
            <p className="text-text-dim text-[13.5px]">This settings key has no fields yet. Add a key below to seed it.</p>
          )}
          <div>
            <AddSettingsField onAdd={(name, val) => update(name, val)} />
          </div>
          {msg && <div className="text-[13px] text-mauve">{msg}</div>}
          <div className="flex items-center gap-3">
            <Button onClick={save} disabled={saving} data-testid="settings-save">{saving ? "Saving…" : "Save settings"}</Button>
            <Button variant="ghost" onClick={load}>Reset</Button>
          </div>
        </CardBody></Card>
      )}
    </>
  );
}

function AddSettingsField({ onAdd }) {
  const [name, setName] = useState("");
  const [val, setVal] = useState("");
  return (
    <div className="grid grid-cols-[1fr_1fr_auto] gap-3 mt-4 pt-4 border-t border-line">
      <input placeholder="Field name" value={name} onChange={(e) => setName(e.target.value)} className="bg-surface border border-line-2 rounded-xl px-3 py-2 text-[13.5px] focus:outline-none focus:border-mauve" data-testid="settings-new-field-name" />
      <input placeholder="Value" value={val} onChange={(e) => setVal(e.target.value)} className="bg-surface border border-line-2 rounded-xl px-3 py-2 text-[13.5px] focus:outline-none focus:border-mauve" data-testid="settings-new-field-value" />
      <Button size="sm" variant="ghost" onClick={() => { if (name) { onAdd(name, val); setName(""); setVal(""); } }} data-testid="settings-add-field">Add field</Button>
    </div>
  );
}

function SystemHealth() {
  const { data, err, reload } = useFetch("/system/admin-status");
  const sys = useFetch("/system/status");
  if (err) return <ErrorState description={err} retry={reload} />;
  if (!data || !sys.data) return <LoadingState />;
  const mongoOk = sys.data.checks?.mongo?.ok;
  return (
    <>
      <PageHeader eyebrow="System" title="Health" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card variant="elevated" data-testid="health-api"><CardBody><div className="flex items-center justify-between"><span className="text-text-dim font-mono text-[11px] uppercase tracking-[0.16em]">API</span><Badge variant="success">{sys.data.status}</Badge></div><div className="font-display text-[28px] mt-2">{sys.data.service}</div></CardBody></Card>
        <Card variant="elevated" data-testid="health-mongo"><CardBody><div className="flex items-center justify-between"><span className="text-text-dim font-mono text-[11px] uppercase tracking-[0.16em]">MongoDB</span><Badge variant={mongoOk ? "success" : "danger"}>{mongoOk ? "Connected" : "Offline"}</Badge></div><div className="font-display text-[28px] mt-2">{data.collections} colls</div><div className="text-[12px] text-text-faint mt-1">{data.indexes} indexes</div></CardBody></Card>
        <Card variant="elevated" data-testid="health-storage"><CardBody><div className="flex items-center justify-between"><span className="text-text-dim font-mono text-[11px] uppercase tracking-[0.16em]">Storage</span><Badge variant="success">Healthy</Badge></div><div className="font-display text-[28px] mt-2">{data.storage_size_mb} MB</div></CardBody></Card>
      </div>
    </>
  );
}

function HomepageBuilder() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/homepage/sections").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const toggle = async (id, enabled) => { await api.patch(`/admin/homepage/sections/${id}`, { enabled: !enabled }); load(); };
  const move = async (id, dir) => { await api.post(`/admin/homepage/sections/${id}/move?dir=${dir}`); load(); };
  if (!items) return <LoadingState />;
  return (
    <>
      <PageHeader eyebrow="Content" title="Homepage builder" lede="Re-order and toggle each section visible on the public homepage." />
      <Card variant="elevated"><CardBody className="p-0"><ul className="divide-y divide-line" data-testid="homepage-sections">
        {items.length === 0 && <li className="px-5 py-6 text-text-dim text-[13.5px]">No sections defined yet.</li>}
        {items.map((s) => (
          <li key={s.id} className="px-5 py-4 flex items-center gap-4" data-testid={`section-${s.kind}`}>
            <span className="font-mono text-[11px] text-text-faint w-8">#{s.order}</span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[14px]">{s.title || s.kind}</div>
              <div className="text-[12px] text-text-faint font-mono uppercase tracking-[0.14em]">{s.kind}</div>
            </div>
            <Badge size="sm" variant={s.enabled ? "success" : "muted"}>{s.enabled ? "Visible" : "Hidden"}</Badge>
            <button onClick={() => move(s.id, "up")} className="text-text-dim hover:text-text text-[12px]" data-testid={`section-up-${s.kind}`}>↑</button>
            <button onClick={() => move(s.id, "down")} className="text-text-dim hover:text-text text-[12px]" data-testid={`section-down-${s.kind}`}>↓</button>
            <Button size="sm" variant="ghost" onClick={() => toggle(s.id, s.enabled)} data-testid={`section-toggle-${s.kind}`}>{s.enabled ? "Hide" : "Show"}</Button>
          </li>
        ))}
      </ul></CardBody></Card>
    </>
  );
}

function LegalPages() {
  const [key, setKey] = useState("privacy");
  const [page, setPage] = useState(null);
  const [msg, setMsg] = useState(null);
  useEffect(() => { api.get(`/legal/${key}`).then(r => setPage(r.data)).catch(() => setPage({ key, title: key, body_html: "" })); }, [key]);
  const onSave = async () => {
    try { await api.put(`/admin/legal/${key}`, { title: page.title, body_html: page.body_html }); setMsg("Saved."); }
    catch (e) { setMsg(e.response?.data?.detail || e.message); }
  };
  return (
    <>
      <PageHeader eyebrow="Content" title="Legal pages" lede="Privacy, Terms, Refund — published live to the public site." />
      <div className="flex gap-2 flex-wrap mb-5">
        {["privacy", "terms", "refund-policy", "accessibility"].map(k => (
          <Button key={k} size="sm" variant={k === key ? "primary" : "ghost"} onClick={() => setKey(k)} data-testid={`legal-tab-${k}`}>{k}</Button>
        ))}
      </div>
      {!page ? <LoadingState /> : (
        <Card variant="elevated"><CardBody className="p-7 space-y-4">
          <div><label className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim block mb-1">Title</label>
            <input className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-mauve" value={page.title || ""} onChange={(e) => setPage({ ...page, title: e.target.value })} data-testid="legal-title" /></div>
          <div><label className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim block mb-1">Body HTML</label>
            <textarea rows={20} className="w-full bg-surface border border-line-2 rounded-xl px-4 py-3 text-[13px] font-mono focus:outline-none focus:border-mauve" value={page.body_html || ""} onChange={(e) => setPage({ ...page, body_html: e.target.value })} data-testid="legal-body" /></div>
          {msg && <div className="text-[13px] text-mauve">{msg}</div>}
          <Button onClick={onSave} data-testid="legal-save">Save & publish</Button>
        </CardBody></Card>
      )}
    </>
  );
}

function MediaLibrary() {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/media?limit=60").then(r => setData(r.data)).catch(() => setData({ items: [] }));
  useEffect(() => { load(); }, []);
  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      load();
    } catch (err) { alert(err.response?.data?.detail || err.message); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const onDelete = async (id) => { if (!window.confirm("Delete this media?")) return; await api.delete(`/media/${id}`); load(); };
  if (!data) return <LoadingState />;
  return (
    <>
      <PageHeader eyebrow="Assets" title="Media library"
        actions={<label className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-mauve text-white text-[13px] cursor-pointer hover:bg-mauve-2" data-testid="media-upload-btn">
          {uploading ? "Uploading…" : "+ Upload"}<input type="file" className="hidden" onChange={onUpload} data-testid="media-upload-input" />
        </label>} />
      {data.items.length === 0 ? <EmptyState icon={ImageIcon} title="No media yet" description="Upload images, PDFs and documents to use across the platform." /> : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="media-grid">
          {data.items.map((m) => (
            <Card key={m.id} variant="default" className="overflow-hidden">
              <div className="aspect-square bg-surface relative">
                {m.kind === "image" && m.url ? (
                  <img src={m.url.startsWith("http") ? m.url : `${process.env.REACT_APP_BACKEND_URL}${m.url}`} alt={m.alt || m.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-text-faint text-[11px] font-mono uppercase tracking-[0.16em]">{m.mime_type}</div>
                )}
              </div>
              <CardBody className="p-3">
                <div className="text-[11px] truncate font-mono text-text-faint">{m.name}</div>
                <button className="text-[11px] text-red-400 mt-1 hover:underline" onClick={() => onDelete(m.id)} data-testid={`media-delete-${m.id}`}>Delete</button>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

function RolesEditor() {
  const [data, setData] = useState(null);
  const [perms, setPerms] = useState([]);
  const [active, setActive] = useState(null);
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/roles"), api.get("/permissions")]).then(([r, p]) => {
      setData(r.data.items); setPerms(p.data.items); setActive(r.data.items[0]);
    }).catch(() => setData([]));
  }, []);
  const togglePerm = (key) => {
    if (!active) return;
    const has = (active.permissions || []).includes(key);
    setActive({ ...active, permissions: has ? active.permissions.filter(p => p !== key) : [...(active.permissions || []), key] });
  };
  const save = async () => {
    try { await api.patch(`/roles/${active.key}`, { label: active.label, description: active.description, permissions: active.permissions }); setMsg("Saved."); }
    catch (e) { setMsg(e.response?.data?.detail || e.message); }
  };
  if (!data) return <LoadingState />;
  return (
    <>
      <PageHeader eyebrow="People" title="Roles & permissions" lede="Edit role definitions and the permissions assigned to each." />
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
        <Card variant="elevated"><CardBody className="p-0"><ul className="divide-y divide-line" data-testid="roles-list">
          {data.map(r => (
            <li key={r.key}><button onClick={() => { setActive(r); setMsg(null); }}
              className={`w-full text-left px-4 py-3 text-[13.5px] hover:bg-surface ${active?.key === r.key ? "bg-mauve/10" : ""}`}
              data-testid={`role-${r.key}`}>
              <div className="font-semibold">{r.label}</div>
              <div className="text-[11px] text-text-faint font-mono">{r.key} · {(r.permissions || []).length} perms</div>
            </button></li>
          ))}
        </ul></CardBody></Card>
        {active && (
          <Card variant="elevated"><CardBody className="p-7">
            <h3 className="font-display text-[24px]">{active.label}</h3>
            <p className="text-text-dim text-[13.5px] mt-2">{active.description}</p>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[440px] overflow-auto pr-2" data-testid="perm-list">
              {perms.map(p => (
                <label key={p.key} className="flex items-start gap-2 p-2 rounded-lg hover:bg-surface cursor-pointer">
                  <input type="checkbox" checked={(active.permissions || []).includes(p.key)} onChange={() => togglePerm(p.key)} data-testid={`perm-${p.key}`} className="mt-1" />
                  <div className="min-w-0 text-[12.5px]"><div className="font-mono text-[11px] text-text-faint uppercase tracking-[0.14em]">{p.group}</div><div className="font-semibold">{p.key}</div><div className="text-text-dim text-[11.5px]">{p.label}</div></div>
                </label>
              ))}
            </div>
            {msg && <div className="text-[13px] text-mauve mt-3">{msg}</div>}
            <Button onClick={save} className="mt-5" data-testid="role-save">Save role</Button>
          </CardBody></Card>
        )}
      </div>
    </>
  );
}

function Stub({ title, description }) {
  return <><PageHeader eyebrow="Coming next" title={title} lede={description} /><EmptyState title="In development" description="This admin view will activate soon. Backend models, endpoints and patterns are already in place." /></>;
}

// ============================================================================
// Categories admin — Courses / Products / Services / Sadhana / Research
// ============================================================================
const CATEGORY_KINDS = [
  { key: "courses", label: "Courses" },
  { key: "products", label: "Products" },
  { key: "services", label: "Services" },
  { key: "sadhana", label: "Sadhana" },
  { key: "research", label: "Research" },
];

function CategoriesAdmin() {
  const [kind, setKind] = useState("courses");
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);   // {id?, name, description, order, visible, featured}
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const load = () => api.get(`/admin/${kind}/categories`).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { setItems(null); load(); }, [kind]);  // eslint-disable-line

  const startNew = () => setEditing({ name: "", description: "", order: 0, visible: true, featured: false });

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const payload = { name: editing.name, description: editing.description || null,
                        order: Number(editing.order) || 0, visible: !!editing.visible, featured: !!editing.featured };
      if (editing.id) await api.patch(`/admin/${kind}/categories/${editing.id}`, payload);
      else await api.post(`/admin/${kind}/categories`, payload);
      setEditing(null); load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    await api.delete(`/admin/${kind}/categories/${id}`); load();
  };

  return (
    <>
      <PageHeader eyebrow="Taxonomy" title="Categories"
        lede="Manage the categories that appear as dropdowns when creating catalog items."
        actions={<Button onClick={startNew} data-testid="cat-new-btn">+ New category</Button>} />
      <div className="flex gap-2 flex-wrap mb-6" data-testid="cat-kind-tabs">
        {CATEGORY_KINDS.map((k) => (
          <Button key={k.key} size="sm" variant={k.key === kind ? "primary" : "ghost"}
            onClick={() => setKind(k.key)} data-testid={`cat-tab-${k.key}`}>{k.label}</Button>
        ))}
      </div>
      {!items ? <LoadingState /> : items.length === 0 ? (
        <EmptyState title="No categories yet" description="Create your first category for this kind." />
      ) : (
        <Card variant="elevated"><CardBody className="p-0"><table className="w-full text-[13.5px]" data-testid="cat-table">
          <thead><tr className="text-left border-b border-line">
            <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim">Name</th>
            <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim">Slug</th>
            <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim">Order</th>
            <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim">Visible</th>
            <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim text-right">Actions</th>
          </tr></thead>
          <tbody>{items.map((c) => (
            <tr key={c.id} className="border-b border-line" data-testid={`cat-row-${c.slug}`}>
              <td className="px-4 py-3 font-semibold">{c.name}</td>
              <td className="px-4 py-3 text-text-faint font-mono text-[11.5px]">{c.slug}</td>
              <td className="px-4 py-3">{c.order}</td>
              <td className="px-4 py-3"><Badge size="sm" variant={c.visible === false ? "muted" : "success"}>{c.visible === false ? "hidden" : "visible"}</Badge></td>
              <td className="px-4 py-3 text-right space-x-3">
                <button onClick={() => setEditing({ ...c })} className="text-mauve hover:underline text-[12px]" data-testid={`cat-edit-${c.slug}`}>Edit</button>
                <button onClick={() => remove(c.id)} className="text-red-400 hover:underline text-[12px]" data-testid={`cat-delete-${c.slug}`}>Delete</button>
              </td>
            </tr>
          ))}</tbody>
        </table></CardBody></Card>
      )}

      {editing && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-auto p-6" role="dialog" data-testid="cat-modal">
          <Card variant="elevated" className="w-full max-w-md my-12"><CardBody className="p-6 space-y-4">
            <h3 className="font-display text-[20px]">{editing.id ? "Edit" : "Create"} {CATEGORY_KINDS.find(k => k.key === kind)?.label} category</h3>
            <div><label className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim block mb-1">Name</label>
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="cat-name"
                className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-mauve" /></div>
            <div><label className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim block mb-1">Description</label>
              <textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="cat-desc"
                className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[13.5px] focus:outline-none focus:border-mauve" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim block mb-1">Order</label>
                <input type="number" value={editing.order || 0} onChange={(e) => setEditing({ ...editing, order: e.target.value })}
                  className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-mauve" /></div>
              <div className="flex items-end gap-4">
                <label className="flex items-center gap-2 text-[13px]"><input type="checkbox" checked={!!editing.visible} onChange={(e) => setEditing({ ...editing, visible: e.target.checked })} /> Visible</label>
                <label className="flex items-center gap-2 text-[13px]"><input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Featured</label>
              </div>
            </div>
            {err && <div className="text-red-400 text-[13px] bg-red-500/10 rounded-xl px-4 py-2">{err}</div>}
            <div className="flex items-center gap-3 pt-2">
              <Button onClick={save} disabled={saving || !editing.name} data-testid="cat-save">{saving ? "Saving…" : "Save"}</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </CardBody></Card>
        </div>
      )}
    </>
  );
}

// ============================================================================
// Banners admin — homepage hero carousel
// ============================================================================
function BannersAdmin() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const load = () => api.get("/admin/homepage/banners").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ title: "", subtitle: "", image_url: "", link_url: "", cta_label: "Learn more", order: 0, enabled: true });

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      if (editing.id) await api.patch(`/admin/homepage/banners/${editing.id}`, editing);
      else await api.post("/admin/homepage/banners", editing);
      setEditing(null); load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    await api.delete(`/admin/homepage/banners/${id}`); load();
  };
  const toggle = async (b) => { await api.patch(`/admin/homepage/banners/${b.id}`, { enabled: !b.enabled }); load(); };

  return (
    <>
      <PageHeader eyebrow="Homepage" title="Hero banners"
        lede="Rotating banners shown at the top of the homepage. Add festivals, launches or announcements."
        actions={<Button onClick={startNew} data-testid="banner-new-btn">+ New banner</Button>} />
      {!items ? <LoadingState /> : items.length === 0 ? (
        <EmptyState title="No banners yet" description="Create your first hero banner." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="banner-list">
          {items.map((b) => (
            <Card key={b.id} variant="elevated" className="overflow-hidden" data-testid={`banner-${b.id}`}>
              <div className="aspect-[21/9] bg-surface relative">
                {b.image_url && <img src={b.image_url.startsWith("http") ? b.image_url : `${process.env.REACT_APP_BACKEND_URL}${b.image_url}`} alt={b.title} className="w-full h-full object-cover" />}
                {!b.enabled && <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-[12px] font-mono uppercase tracking-[0.2em]">disabled</div>}
              </div>
              <CardBody>
                <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-text-faint">{b.subtitle}</div>
                <div className="font-display text-[18px] mt-1">{b.title || "Untitled banner"}</div>
                <div className="text-[12px] text-text-dim mt-2">Order: {b.order} · CTA: {b.cta_label || "—"} · Link: {b.link_url || "—"}</div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => setEditing({ ...b })} className="text-mauve hover:underline text-[12px]" data-testid={`banner-edit-${b.id}`}>Edit</button>
                  <button onClick={() => toggle(b)} className="text-mauve hover:underline text-[12px]" data-testid={`banner-toggle-${b.id}`}>{b.enabled ? "Disable" : "Enable"}</button>
                  <button onClick={() => remove(b.id)} className="text-red-400 hover:underline text-[12px]" data-testid={`banner-delete-${b.id}`}>Delete</button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-auto p-6" role="dialog" data-testid="banner-modal">
          <Card variant="elevated" className="w-full max-w-lg my-12"><CardBody className="p-6 space-y-4">
            <h3 className="font-display text-[20px]">{editing.id ? "Edit banner" : "New banner"}</h3>
            {[
              ["title", "Title"], ["subtitle", "Subtitle / eyebrow"],
              ["cta_label", "CTA label"], ["link_url", "Link URL (external or /path)"],
            ].map(([k, label]) => (
              <div key={k}><label className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim block mb-1">{label}</label>
                <input value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} data-testid={`banner-field-${k}`}
                  className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-mauve" /></div>
            ))}
            <div><label className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim block mb-1">Image</label>
              <BannerImageField value={editing.image_url || ""} onChange={(v) => setEditing({ ...editing, image_url: v })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim block mb-1">Order</label>
                <input type="number" value={editing.order || 0} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })}
                  className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-mauve" /></div>
              <label className="flex items-end gap-2 text-[13px] pb-2"><input type="checkbox" checked={!!editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} /> Enabled</label>
            </div>
            {err && <div className="text-red-400 text-[13px] bg-red-500/10 rounded-xl px-4 py-2">{err}</div>}
            <div className="flex items-center gap-3 pt-2">
              <Button onClick={save} disabled={saving || !editing.image_url} data-testid="banner-save">{saving ? "Saving…" : "Save"}</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </CardBody></Card>
        </div>
      )}
    </>
  );
}

// Lightweight image picker/uploader for banners (uses same Media Library API)
function BannerImageField({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const resolve = (u) => (!u ? "" : u.startsWith("http") ? u : apiBase + u);
  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const u = r.data?.url ? (r.data.url.startsWith("http") ? r.data.url : apiBase + r.data.url) : "";
      if (u) onChange(u);
    } catch (err) { alert(err.response?.data?.detail || err.message); }
    finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div className="flex items-stretch gap-2">
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Paste image URL or upload →"
        className="flex-1 bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[13.5px] focus:outline-none focus:border-mauve" data-testid="banner-image-url" />
      <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-line-2 bg-surface text-[12.5px] cursor-pointer hover:border-mauve">
        {uploading ? "…" : "Upload"}
        <input type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="banner-image-upload" />
      </label>
      {value && <img src={resolve(value)} alt="" className="w-14 h-14 rounded-lg object-cover border border-line-2" />}
    </div>
  );
}

// ============================================================================
// Brochures admin — 4 fixed slots, upload/replace PDFs
// ============================================================================
const BROCHURE_SLOTS = [
  { slot: "rare-products", label: "Rare Products Brochure" },
  { slot: "complete-products", label: "Complete Products Brochure" },
  { slot: "complete-courses", label: "Complete Courses Brochure" },
  { slot: "services", label: "Services Brochure" },
];

function BrochuresAdmin() {
  const [items, setItems] = useState(null);
  const [uploading, setUploading] = useState(null);
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";

  const load = () => api.get("/homepage/brochures").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const onUpload = async (slot, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(slot);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/admin/homepage/brochures/${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      load();
    } catch (err) { alert(err.response?.data?.detail || err.message); }
    finally { setUploading(null); e.target.value = ""; }
  };
  const clear = async (slot) => {
    if (!window.confirm("Clear this brochure?")) return;
    await api.delete(`/admin/homepage/brochures/${slot}`); load();
  };
  const resolve = (u) => (!u ? "" : u.startsWith("http") ? u : apiBase + u);

  return (
    <>
      <PageHeader eyebrow="Downloads" title="Brochures" lede="Upload the PDFs shown in the homepage Downloads section." />
      {!items ? <LoadingState /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="brochure-admin-list">
          {items.map((b) => (
            <Card key={b.slot} variant="elevated"><CardBody className="p-5">
              <div className="flex items-start gap-3">
                <span className="w-11 h-11 rounded-xl border border-line-2 bg-glass grid place-items-center text-mauve"><FileText size={20} /></span>
                <div className="flex-1">
                  <div className="font-display text-[16px] leading-tight">{b.label}</div>
                  <div className="text-[12px] text-text-faint mt-1">{b.url ? b.name : "Not uploaded"}</div>
                  {b.url && <a href={resolve(b.url)} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-[12.5px] text-mauve hover:underline">Preview / Download</a>}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-line-2 bg-surface text-[12.5px] cursor-pointer hover:border-mauve">
                  <Upload size={13} /> {uploading === b.slot ? "Uploading…" : (b.url ? "Replace PDF" : "Upload PDF")}
                  <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => onUpload(b.slot, e)} data-testid={`brochure-upload-${b.slot}`} />
                </label>
                {b.url && <button onClick={() => clear(b.slot)} className="text-red-400 text-[12px] hover:underline" data-testid={`brochure-clear-${b.slot}`}>Clear</button>}
              </div>
            </CardBody></Card>
          ))}
        </div>
      )}
    </>
  );
}

export function AdminConsole() {
  return (
    <DashboardLayout nav={ADMIN_NAV} title="Admin">
      <Routes>
        <Route index element={<AdminOverview />} />
        <Route path="system-health" element={<SystemHealth />} />
        <Route path="courses" element={<EntityList title="Courses" listPath="/admin/courses" detailPath="/courses/:slug" kind="course" />} />
        <Route path="sadhana" element={<EntityList title="Sadhana" listPath="/admin/sadhana" detailPath="/sadhana/:slug" kind="sadhana" />} />
        <Route path="products" element={<EntityList title="Products" listPath="/admin/products" detailPath="/products/:slug" kind="product" />} />
        <Route path="services" element={<EntityList title="Services" listPath="/admin/services" detailPath="/services/:slug" kind="service" />} />
        <Route path="videos" element={<EntityList title="Videos" listPath="/admin/videos" kind="video" />} />
        <Route path="enquiries" element={<EnquiriesAdmin />} />
        <Route path="research" element={<EntityList title="Research" listPath="/admin/research" detailPath="/research/:slug" kind="research" />} />
        <Route path="library" element={<EntityList title="Library" listPath="/admin/books" detailPath="/library/:slug" kind="book" />} />
        <Route path="orders" element={<EntityList title="Orders" listPath="/admin/orders" kind="order" />} />
        <Route path="bookings" element={<EntityList title="Bookings" listPath="/admin/bookings" kind="booking" />} />
        <Route path="users" element={<UsersList />} />
        <Route path="audit" element={<AuditList />} />
        <Route path="settings" element={<SettingsAdmin />} />
        <Route path="homepage" element={<HomepageBuilder />} />
        <Route path="banners" element={<BannersAdmin />} />
        <Route path="brochures" element={<BrochuresAdmin />} />
        <Route path="categories" element={<CategoriesAdmin />} />
        <Route path="legal" element={<LegalPages />} />
        <Route path="media" element={<MediaLibrary />} />
        <Route path="roles" element={<RolesEditor />} />
        <Route path="*" element={<Stub title="Coming soon" description="" />} />
      </Routes>
    </DashboardLayout>
  );
}
